import { Injectable } from '@angular/core';
import camelCase from 'lodash-es/camelCase';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { environment } from 'src/environments/environment';
import { Router, RoutesRecognized } from '@angular/router';
import { first, map, tap } from 'rxjs/operators';

export type Context =
  | 'header'
  | 'auth'
  | 'project'
  | 'projectTemplate'
  | 'dashboard'
  | 'projectForm'
  | 'insights'
  | 'adminApiKeyForm'
  | 'skill'
  | 'designer'
  | 'manageSkills'
  | 'editSkill'
  | 'createSkill'
  | 'helpCenter'
  | 'ecosystem'
  | 'projectPreview'
  | 'mediaAssetCreator'
  | 'mediaRequestPopover'
  | 'upgradeDialog'
  | 'upgradeSubscription'
  | 'usageIndicatorTooltip'
  | 'accountTeam';

type Action =
  | 'clicked'
  | 'changed'
  | 'created'
  | 'saved'
  | 'updated'
  | 'deleted'
  | 'loggedOut'
  | 'opened'
  | 'closed'
  | 'copied'
  | 'confirmed'
  | 'cancelled'
  | 'submitted'
  | 'upload'
  | 'errored';

export interface AnalyticsEvent {
  context: Context; // Where is the event from?
  object: string; // What is the event effecting?
  action: Action; // What was the action taken?
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  constructor(private analytics: AnalyticsBrowser, private router: Router) {
    this.captureAnonymousIdFromQueryParams();
  }

  private captureAnonymousIdFromQueryParams() {
    this.router.events
      .pipe(
        // we only care if the params are present when the app first loads
        first((event) => event instanceof RoutesRecognized),
        // get the anonymous_id from the query params
        map((event: RoutesRecognized) => event.state.root.queryParams['anonymous_id']),
        // set the anonymous_id in segment
        tap((id: string) => id && this.analytics.setAnonymousId(id)),
      )
      .subscribe();
  }

  public startTracking() {
    const writeKey = environment.segment.writeKey;

    // if no writeKey is provided, do not load thus no events will be tracked
    if (writeKey) {
      this.analytics.load({ writeKey });
    }
  }

  track(event: AnalyticsEvent, properties?: Record<string, unknown>) {
    const projectName = 'ddnaStudio';
    this.analytics.track(
      `${projectName}:${event.context}_${camelCase(event.object)}_${event.action}`,
      properties,
    );
  }

  page(url: string) {
    this.analytics.page(url);
  }

  // Identifies the user to segment
  setUserProperties(userId: string, traits?: Record<string, unknown>) {
    this.analytics.identify(userId, traits);
  }

  // Clears the user stored segment user in localstorage
  unsetUserProperties() {
    this.analytics.reset();
  }
}
